/**
 * EchoScheduler (real-mode)
 * ──────────────────────────────────────────────────────────────────────
 * Route split (2026-04-21): this file handles REAL-MODE traffic only.
 * Demo-mode callers are delegated to `LegacyEchoScheduler` via the
 * `SchedulerRoute` wrapper at the route boundary (see App.js). When called
 * directly, we still guard defensively so no demo fixtures can leak into a
 * real-mode render path even if the caller forgets to use the wrapper.
 *
 * v2 edits (scheduler_edits_v2.md) applied in real mode:
 *   - Today view: TodaySlotGrid (vendor-parity open + booked cards) +
 *                 compact PunchListRail. All scope params (entity_id +
 *                 location_id) are resolved before `/slots/available` is
 *                 called — vendor §17 rejects partial scope with 422.
 *   - Rooming: RoomingByRole dispatches FD/MA/Provider/Manager views
 *              (API-backed data; empty state when no data).
 *   - Waitlist: WaitlistByRole (empty state until waitlist API wired).
 *   - Settings: Overbooking section rendered empty-state until API wired.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { useAppMode } from '../../contexts/AppModeContext';
import { isDemoMode } from '../../lib/modeGuard';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';
import * as schedulerApi from '../../lib/api/schedulerApi';
import {
  Plus, Sparkles, ScanLine, Users, UserPlus, Calendar as CalIcon,
  CalendarDays, DoorOpen, ClipboardList, PhoneCall, LineChart, Settings as SettingsIcon, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import SchedulerCalendar from '../../components/echo/scheduler/SchedulerCalendar';
import RealCampaignRecallsPanel from '../../components/echo/recalls/RealCampaignRecallsPanel';
import { SchedulerTraceProvider, useSchedulerTraceString, useReportSchedulerSource } from '../../lib/scheduler/SchedulerTraceContext';
import BookingModal from '../../components/echo/scheduler/BookingModal';
import { AnalyticsTab } from '../../components/echo/scheduler/SchedulerTabs';
import RealOverbookingDashboard from '../../components/echo/scheduler/v2/RealOverbookingDashboard';
import SchedulerSettings from '../../components/echo/scheduler/SchedulerSettings';
import SchedulerAIChat from '../../components/echo/scheduler/SchedulerAIChat';
import LensUploadLightbox from '../../components/shared/LensUploadLightbox';
import LegacyEchoScheduler from './LegacyEchoScheduler';
import TodaySlotGrid from '../../components/echo/scheduler/TodaySlotGrid';
import PunchListRail from '../../components/echo/scheduler/v2/PunchListRail';
import PunchListCategoryDialog from '../../components/echo/scheduler/v2/PunchListCategoryDialog';
import RealRoomingTab from '../../components/echo/scheduler/v2/RealRoomingTab';
import RealWaitlistTab from '../../components/echo/scheduler/v2/RealWaitlistTab';
import CancelRescheduleModal from '../../components/echo/scheduler/CancelRescheduleModal';
import AddToWaitlistPanel from '../../components/echo/scheduler/v2/AddToWaitlistPanel';
import WeeklyReview from '../../components/echo/scheduler/v2/WeeklyReview';
import { resolveSchedulerRole } from '../../lib/schedulerRole';
import {
  normalizeAppointments as adapterNormalizeAppointments,
  normalizeSlots as adapterNormalizeSlots,
  mergeSlotsAndAppointments,
} from '../../lib/api/schedulerAdapter';

/* ── punch-list normalizer (API shape → UI shape; no banned literals) ── */
const normalizePunchItem = (it, idx) => ({
  id: it.id || `pl-${idx}`,
  priority: it.priority || 'blue',
  patient: it.patient || it.patient_name || null,
  time: it.time || it.time_label || null,
  issue: it.issue || it.message || '',
  financial: it.financial || it.financial_label || null,
  action: it.action || null,
  actionType: it.action_type || it.actionType || null,
  echoHandling: it.echo_handling || it.echoHandling || null,
});

const todayIsoString = () => new Date().toISOString().slice(0, 10);

/* ── Tab section header (parity lift from Legacy `Card` chrome) ── */
const TabSection = ({ icon: Icon, title, subtitle, meta, testId, children }) => (
  <div className="border border-slate-200 bg-white rounded-lg overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
        <p className="text-sm font-semibold text-slate-800 truncate" data-testid={`${testId}-title`}>{title}</p>
        {subtitle && (
          <span className="text-[10px] text-slate-400 truncate" data-testid={`${testId}-subtitle`}>{subtitle}</span>
        )}
      </div>
      {meta && (
        <div className="text-[10px] text-slate-400 shrink-0" data-testid={`${testId}-meta`}>{meta}</div>
      )}
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const EchoScheduler = () => {
  const { isDemo } = useAppMode();
  // Defensive delegate: consult both the context AND a direct localStorage
  // read. Either truthy answer means demo — the only way real-mode API calls
  // fire is if BOTH agree we're in real mode.
  if (isDemo || isDemoMode()) return <LegacyEchoScheduler />;
  // Wrap real mode in the trace provider so each tab can register its
  // own data-*-source state and the EchoScheduler root can roll them
  // up into a single `data-scheduler-trace` pipe string.
  return (
    <SchedulerTraceProvider>
      <RealEchoScheduler />
    </SchedulerTraceProvider>
  );
};

/* ═══ REAL-MODE COMPONENT (API-backed, no demo literals reachable) ═══ */
/**
 * RealEchoScheduler — real-mode scheduler shell.
 *
 * Optional capture props (used by /echo/scheduler/_trace?capture=N):
 *   captureMode        — boolean. When true, sequentially cycles
 *                        `setActiveTab` through every TABS entry once
 *                        per run, with a fixed dwell time, then signals
 *                        completion. NO PHI is emitted — orchestration
 *                        only drives tab state; data observation lives
 *                        in the trace-context + endpointCounters.
 *   captureRuns        — integer (capped client-side by the caller).
 *                        How many full TABS sweeps to perform.
 *   onCaptureProgress  — ({ run, tabIndex, tabId }) => void
 *   onCaptureComplete  — ({ runs, tabsVisited }) => void
 */
export const RealEchoScheduler = ({
  captureMode = false,
  captureRuns = 1,
  captureTabDwellMs = 800,
  onCaptureProgress,
  onCaptureComplete,
} = {}) => {
  const { user } = useAuth();
  const { canSchedulerWrite, canSchedulerAdmin } = capabilitiesFor(user);
  const allowBooking = !!canSchedulerWrite;
  // Settings tab + admin-only overlays are gated on canSchedulerAdmin
  // (= scheduler:admin capability) which only super_admin/admin hold.
  const allowSettings = !!canSchedulerAdmin;
  // Legacy shape kept so downstream JSX doesn't need to branch — the demo
  // code paths are simply unreachable under `isDemo === false`.
  const isDemo = false;
  const schedulerRole = resolveSchedulerRole({ isDemo, user });
  const [activeTab, setActiveTab] = useState('today');
  const [punchList, setPunchList] = useState([]);
  const [todayCards, setTodayCards] = useState([]);
  const [todaySummary, setTodaySummary] = useState({ open: 0, booked: 0, total: 0 });
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayError, setTodayError] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  // Non-blocking partial-failure warning. Populated only on All-Locations
  // fetches when some shards fail but others succeed. Cleared on every
  // Cleared on every load (success or failure) to avoid stale banners.
  const [availabilityPartialWarning, setAvailabilityPartialWarning] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [bookingWalkin, setBookingWalkin] = useState(false);
  const [lensOpen, setLensOpen] = useState(false);
  const [addWaitlistOpen, setAddWaitlistOpen] = useState(false);
  const [cancelRescheduleApt, setCancelRescheduleApt] = useState(null);
  const [categoryDialog, setCategoryDialog] = useState(null);

  // Scope state — the vendor §17 availability contract requires BOTH
  // entity_id and location_id. These filters drive the availability call
  // and are also inherited by the BookingModal via prefill props.
  const [entityFilter, setEntityFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);
  // First-bootstrap flag — auto-seeded defaults (first entity / first
  // location / user.location_id) only apply on the first fetch. Once the
  // user makes an explicit "All Locations" selection it must survive
  // subsequent refreshes without being clobbered.
  const [bootstrapping, setBootstrapping] = useState(true);

  // Date-nav state — `todayIso` is aliased to `selectedDate` so the
  // scheduler refetches when the user navigates prev/next/today/picker.
  const [selectedDate, setSelectedDate] = useState(todayIsoString());
  const todayIso = selectedDate;

  const fetchToday = useCallback(async () => {
    // Belt-and-braces demo guard: if the mode read flips mid-session,
    // refuse to issue scheduler API calls under demo.
    if (isDemoMode()) {
      setTodayLoading(false);
      setTodayError(null);
      return;
    }
    setTodayLoading(true);
    setTodayError(null);
    setAvailabilityError(null);

    // 1 · Load entities + locations + daily-summary in parallel.
    //     Appointments fire AFTER scope resolution (step 5b) so they can
    //     be scoped to `entity_id` (+ `location_id` for single-location
    //     mode). Calling the enriched endpoint UNSCOPED was the
    //     production OPEN-only bug: some vendor builds short-circuit
    //     the enriched view with `{results: []}` when not given a
    //     provider id, which made `_enrichedFallback` (gated on 404/501)
    //     never fire even though the basic `/appointments` endpoint
    //     would have returned the rows.
    const [entitiesRes, locationsRes, summaryRes] = await Promise.all([
      schedulerApi.listEntities({ active: true }),
      schedulerApi.listLocations({ active: true }),
      schedulerApi.getDailySummary({ date: todayIso }),
    ]);

    // 2 · Normalize entities + locations.
    const rawEnts = entitiesRes?.ok
      ? (entitiesRes.data?.entities || entitiesRes.data?.items || (Array.isArray(entitiesRes.data) ? entitiesRes.data : []))
      : [];
    const mappedEnts = rawEnts
      .map((e) => ({ id: e.id || e._id, name: e.name || e.full_name }))
      .filter((e) => e.id && e.name);
    if (mappedEnts.length) setEntities(mappedEnts);

    const rawLocs = locationsRes?.ok
      ? (locationsRes.data?.locations || locationsRes.data?.items || (Array.isArray(locationsRes.data) ? locationsRes.data : []))
      : [];
    const mappedLocs = rawLocs
      .map((l) => ({ id: l.id || l._id, name: l.name || l.location_name }))
      .filter((l) => l.id && l.name);
    if (mappedLocs.length) setLocations(mappedLocs);

    // 3 · Resolve scope — priority ladder:
    //       provider: explicit filter → user.entity_id → first entity
    //       location: explicit filter → user.location_id → first location
    //     (Only the first-bootstrap pass seeds `setLocationFilter`; an
    //     explicit "All Locations" selection survives subsequent fetches.)
    const effectiveEntityId = entityFilter || user?.entity_id || (mappedEnts.length ? mappedEnts[0].id : '');
    let effectiveLocationId = locationFilter || user?.location_id;
    if (!effectiveLocationId && mappedLocs.length) effectiveLocationId = mappedLocs[0].id;

    if (bootstrapping && !locationFilter && effectiveLocationId) setLocationFilter(effectiveLocationId);
    if (bootstrapping && !entityFilter && effectiveEntityId) setEntityFilter(effectiveEntityId);

    // 4 · Vendor §17 requires BOTH entity_id AND location_id.
    const haveScope = !!(effectiveEntityId && effectiveLocationId);
    const availabilityParams = {
      date: todayIso,
      entity_id: effectiveEntityId,
      location_id: effectiveLocationId,
    };

    // 5 · All-Locations discriminator.
    //    Post-bootstrap, an explicit empty `locationFilter` + resolved
    //    `effectiveEntityId` means "show every location" — use the
    //    vendor §17 /slots/available/by-location endpoint to fetch every
    //    location's slots in a single round trip. The previous N-way
    //    Promise.all fan-out is gone; normalization / merge logic below
    //    still iterates per-location shards so the UI-state shape is
    //    unchanged.
    const locationList = mappedLocs.length ? mappedLocs : locations;
    const isAllLocations = !bootstrapping && !locationFilter && locationList.length > 0 && !!effectiveEntityId;

    // 5b · Appointments — fire AFTER scope resolution so the request
    // carries `entity_id` (+ `location_id` for single-location mode).
    // The provider-scoped enriched request bypasses the empty-200
    // short-circuit some vendor builds return for unscoped requests.
    const apptParams = { date: todayIso };
    if (effectiveEntityId) apptParams.entity_id = effectiveEntityId;
    // Pass location only in single-location mode. In all-locations we
    // want the union across every location, not just the bootstrap
    // default's slice.
    if (!isAllLocations && effectiveLocationId) apptParams.location_id = effectiveLocationId;
    const apptsRes = await schedulerApi.listAppointmentsPreferEnriched(apptParams);

    let slotsRes;
    if (isAllLocations) {
      // Single vendor call: GET /api/scheduler/slots/available/by-location
      const byLocRes = await schedulerApi.queryAvailabilityByLocation({
        date: todayIso,
        entity_id: effectiveEntityId,
      });
      slotsRes = { _all: true, byLocation: byLocRes };
    } else {
      // Single-location path — vanilla vendor §17 call gated on haveScope.
      const singleCall = haveScope
        ? schedulerApi.queryAvailability(availabilityParams)
        : Promise.resolve({
            ok: false,
            error: {
              status: 0,
              code: 'MISSING_SCOPE',
              message: 'Select provider and location to load availability.',
              details: { missing: [!effectiveEntityId && 'entity_id', !effectiveLocationId && 'location_id'].filter(Boolean) },
            },
          });
      slotsRes = await singleCall;
    }

    // 6 · Appointments — the vendor §18 list drives the BOOKED overlay.
    if (!apptsRes?.ok) {
      setTodayError(apptsRes?.error || { message: 'Failed to load appointments' });
      setTodayLoading(false);
      return;
    }
    const appointments = adapterNormalizeAppointments(apptsRes.data, todayIso);

    // 6b · SHAPE-DRIFT GUARD — vendor occasionally ships /appointments
    // wrapped in `{ results: [...] }` or `{ data: { appointments: [...] } }`
    // instead of the canonical `{ appointments: [...] }`. Our extractor
    // accepts every variant, but if the raw payload has obvious record-ish
    // keys yet the normalized list comes back empty, log a structured
    // warning so the discrepancy surfaces in production traces.
    if (apptsRes?.ok && appointments.length === 0) {
      const raw = apptsRes.data;
      const rawKeys = (raw && typeof raw === 'object') ? Object.keys(raw) : [];
      const looksLikeRecord = rawKeys.some((k) =>
        ['appointments', 'items', 'results', 'records', 'list', 'rows', 'data', 'total', 'count'].includes(k),
      );
      if (looksLikeRecord) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Scheduler] /appointments returned 0 normalized rows — possible shape drift',
          {
            date: todayIso,
            rawTopLevelKeys: rawKeys,
            rawArrayKeyLengths: rawKeys.reduce((acc, k) => {
              if (Array.isArray(raw[k])) acc[k] = raw[k].length;
              return acc;
            }, {}),
          },
        );
      }
    }

    // 7 · Slots — either a single response OR the merged all-locations set.
    let slots = [];
    let allLocPartialWarning = null;  // non-blocking partial-failure warning
    if (slotsRes && slotsRes._all) {
      // Vendor §17 by-location envelope. Response shape:
      //   { by_location: { [locationId]: { slots: [...], location_name } },
      //     errors?: [{ location_id, message }],
      //     partial_failures?: [...] }
      // Normalize each shard through adapterNormalizeSlots so the UI-state
      // shape is identical to the legacy fan-out merge.
      const merged = [];
      const seen = new Set();
      const byLocRes = slotsRes.byLocation;
      if (byLocRes && !byLocRes.ok) {
        setAvailabilityError(byLocRes.error || null);
      } else if (byLocRes && byLocRes.ok) {
        const data = byLocRes.data || {};
        const byLocationMap = data.by_location || data.byLocation || {};
        // Lookup so we can resolve location_name even when the vendor
        // doesn't stamp it on each slot.
        const locNameById = {};
        for (const l of (mappedLocs.length ? mappedLocs : locations)) {
          locNameById[l.id] = l.name;
        }
        const succeededLocIds = new Set();
        for (const [locId, group] of Object.entries(byLocationMap)) {
          if (!group || (group.error || group.failed)) continue;  // per-shard error → warning below
          const groupLocName = group.location_name || group.locationName || locNameById[locId] || '';
          const rawSlots = (group.slots || group.available_slots) || [];
          // Stamp BOTH location_id AND location_name so the adapter
          // emits a fully-labelled slot card. This is the
          // "Include location label on each slot" guarantee.
          const stampedData = {
            slots: rawSlots.map((s) => ({
              ...s,
              location_id:   s.location_id   || locId,
              location_name: s.location_name || groupLocName,
            })),
          };
          const shard = adapterNormalizeSlots(stampedData, todayIso);
          if (shard.length) succeededLocIds.add(locId);
          for (const s of shard) {
            const key = `${s.startAt || ''}|${s.locationId || ''}|${s.providerId || ''}`;
            if (seen.has(key)) continue;
            seen.add(key);
            if (!s.locationName && s.locationId) {
              s.locationName = locNameById[s.locationId] || groupLocName || '';
            }
            merged.push(s);
          }
        }
        // Partial-failure detection — surface non-blocking warning
        // when SOME shards failed but others returned data.
        const shardErrors = Array.isArray(data.errors) ? data.errors
                          : Array.isArray(data.partial_failures) ? data.partial_failures
                          : [];
        const perShardFailed = Object.values(byLocationMap)
          .filter((g) => g && (g.error || g.failed));
        if (shardErrors.length || perShardFailed.length) {
          const failedNames = [
            ...shardErrors.map((e) => locNameById[e.location_id] || e.location_id || ''),
            ...perShardFailed.map((g) => g.location_name || g.locationName || ''),
          ].filter(Boolean);
          allLocPartialWarning = {
            kind: 'partial_failure',
            failedCount: shardErrors.length + perShardFailed.length,
            succeededCount: succeededLocIds.size,
            failedLocations: failedNames,
            message: failedNames.length
              ? `Couldn't load availability for ${failedNames.length} location(s): ${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? '…' : ''}. Showing the rest.`
              : `Some locations didn't load. Showing partial results.`,
          };
        }
        // Sort ASCENDING by startAt; ties broken by location label.
        merged.sort((a, b) => {
          const av = a.startAt || '';
          const bv = b.startAt || '';
          if (av !== bv) return av < bv ? -1 : 1;
          return (a.locationName || '').localeCompare(b.locationName || '');
        });
      }
      slots = merged;
    } else if (slotsRes && slotsRes.ok) {
      slots = adapterNormalizeSlots(slotsRes.data, todayIso);
    } else if (slotsRes && !slotsRes.ok) {
      // Availability failed (missing scope, vendor 422, etc.) — surface a
      // distinct banner in the grid header rather than clobbering the
      // whole Today view. Appointments still render.
      setAvailabilityError(slotsRes.error || null);
    }
    setAvailabilityPartialWarning(allLocPartialWarning);

    // 8 · Merge slots + appointments into the unified Today grid.
    const { cards, summary } = mergeSlotsAndAppointments(slots, appointments, {
      providerId: effectiveEntityId,
      locationId: isAllLocations ? null : effectiveLocationId,
    });
    setTodayCards(cards);
    setTodaySummary(summary);

    // 8b · DEBUG GUARD — if appointments came back but NO non-open card
    // surfaced, something is wrong with the merge key (e.g. vendor
    // changed `startAt` precision, all-locations shards returned a
    // different `location_id` family than `/appointments`, or the
    // appointment payload has a missing/different provider id).
    // Log a single structured console error so the issue surfaces in
    // production traces without ever blocking the grid.
    const nonOpenCards = cards.filter((c) => c.kind !== 'open');
    if ((appointments?.length || 0) > 0 && nonOpenCards.length === 0) {
      const sampleAppts = appointments.slice(0, 3).map((a) => ({
        id: a.id,
        startAt: a.startAt,
        locationId: a.locationId,
        providerId: a.providerId,
        status: a.status,
        patient: a.patient || a.patientName,
      }));
      const sampleSlots = (slots || []).slice(0, 3).map((s) => ({
        slotId: s.slotId,
        startAt: s.startAt,
        locationId: s.locationId,
        providerId: s.providerId,
      }));
      // eslint-disable-next-line no-console
      console.error(
        '[Scheduler] appointmentsOnSelectedDate > 0 but renderedNonOpenCards === 0 — merge regression',
        {
          date: todayIso,
          providerScope: effectiveEntityId,
          locationScope: isAllLocations ? 'ALL' : effectiveLocationId,
          appointmentCount: appointments.length,
          slotCount: slots.length,
          sampleAppts,
          sampleSlots,
        },
      );
    } else if ((appointments?.length || 0) > 0 && summary.booked === 0) {
      // Original (kept for back-compat with the existing tests that
      // grep for this message). Will not fire if the new structured
      // error above already fired.
      const sampleAppt = appointments[0];
      const sampleSlot = slots[0];
      // eslint-disable-next-line no-console
      console.warn(
        '[Scheduler] appointments fetched but ZERO booked cards rendered — possible merge-key mismatch',
        {
          date: todayIso,
          providerId: effectiveEntityId,
          locationScope: isAllLocations ? 'ALL' : effectiveLocationId,
          appointmentCount: appointments.length,
          slotCount: slots.length,
          sampleApptStart: sampleAppt?.startAt,
          sampleApptLoc:   sampleAppt?.locationId,
          sampleApptProv:  sampleAppt?.providerId,
          sampleSlotStart: sampleSlot?.startAt,
          sampleSlotLoc:   sampleSlot?.locationId,
          sampleSlotProv:  sampleSlot?.providerId,
        },
      );
    }

    // 9 · Punch list — best-effort, failures here don't blank the grid.
    if (summaryRes?.ok) {
      setPunchList((summaryRes.data?.punch_list || summaryRes.data?.items || []).map(normalizePunchItem));
    } else {
      setPunchList([]);
    }

    setTodayLoading(false);
    if (bootstrapping) setBootstrapping(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayIso, entityFilter, locationFilter, user?.entity_id, user?.location_id]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const today = useMemo(() => new Date(), []);
  const redCount = punchList.filter(p => p.priority === 'red').length;
  const amberCount = punchList.filter(p => p.priority === 'amber').length;
  const greenCount = punchList.filter(p => p.priority === 'green').length;

  const handlePunchAction = (item) => {
    if (item.actionType === 'autofill') {
      toast.success('Auto-fill requested…', { description: 'Contacting waitlist patients' });
      return;
    }
    if (item.actionType === 'call') toast.info(`Calling ${item.patient}…`);
    else if (item.actionType === 'scan') toast.info('Opening Lens scanner…');
    else if (item.actionType === 'collect') toast.info(`Collecting copay from ${item.patient}`);
    else if (item.actionType === 'book') toast.info('Opening booking flow…');
  };

  // Booking-intent handlers — CTAs are never `disabled`; we toast the
  // read-only message on click instead of muting the button (dead-button
  // regression fix, see scheduler_cta_contract.test.js).
  const openNewBooking = () => {
    if (!allowBooking) { toast.info('Your role is read-only — ask Front Desk or Admin to book.'); return; }
    setBookingWalkin(false);
    setBookingPrefill(null);
    setBookingOpen(true);
  };
  const openWalkinBooking = () => {
    if (!allowBooking) { toast.info('Your role is read-only — ask Front Desk or Admin to book.'); return; }
    setBookingWalkin(true);
    setBookingPrefill(null);
    setBookingOpen(true);
  };
  const openBookingFromSlot = (card) => {
    if (!allowBooking) { toast.info('Your role is read-only — ask Front Desk or Admin to book.'); return; }
    setBookingWalkin(false);
    setBookingPrefill({
      time: card?.time || null,
      providerId: card?.providerId || null,
      locationId: card?.locationId || null,
    });
    setBookingOpen(true);
  };

  // Inline check-in from a slot card. Vendor §18: PATCH /status with
  // 'checked_in'. We optimistically refetch the day grid so the chip
  // flips from BOOKED → CHECKED-IN without the user having to refresh.
  // Read-only roles see a toast (no muted CTA — dead-button regression
  // contract from scheduler_cta_contract.test.js).
  const handleCheckin = async (card) => {
    if (!allowBooking) { toast.info('Your role is read-only — ask Front Desk or Admin to check in.'); return; }
    if (!card?.appointmentId) return;
    const res = await schedulerApi.checkinAppointment(card.appointmentId);
    if (res?.ok) {
      toast.success(`${card.patient || 'Patient'} checked in`, {
        description: card.time ? `${card.time} · ${card.providerName || ''}`.trim() : undefined,
      });
      fetchToday();
    } else {
      toast.error('Check-in failed', { description: res?.error?.message || 'Try again' });
    }
  };

  const TABS_ALL = [
    { id: 'today', label: 'Today' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'rooming', label: 'Rooming' },
    { id: 'waitlist', label: 'Waitlist' },
    { id: 'recall', label: 'Recall' },
    { id: 'review', label: 'Review' },
    { id: 'analytics', label: 'Insights' },
    { id: 'settings', label: 'Settings' },
  ];
  // Edit 5: MA role hides Waitlist from navigation.
  // Review tab is manager-only.
  // Settings tab is admin-only (scheduler:admin capability) — read_only
  // / front_desk / provider / MA see the tab strip without it so they
  // can't click into a screen that would 403 on every save.
  // Recall is reachable by every role with scheduler:read capability —
  // empty state when API hasn't been provisioned (still PR-ready).
  const TABS = TABS_ALL.filter(t => {
    if (t.id === 'waitlist' && schedulerRole === 'ma') return false;
    if (t.id === 'review' && schedulerRole !== 'manager') return false;
    if (t.id === 'settings' && !allowSettings) return false;
    return true;
  });

  /* ── Capture orchestration (/_trace?capture=N) ──
     Sequentially drive `setActiveTab` through every TABS entry N times.
     Pure orchestration — no PHI is touched here; the trace context and
     endpointCounters singleton observe the resulting traffic on their
     own. Cancellation hooks into unmount via the cleanup flag. */
  useEffect(() => {
    if (!captureMode) return undefined;
    const tabIds = TABS.map((t) => t.id);
    if (tabIds.length === 0) return undefined;
    const runs = Math.max(1, Math.floor(Number(captureRuns) || 1));
    const dwell = Math.max(100, Math.floor(Number(captureTabDwellMs) || 800));
    let cancelled = false;
    (async () => {
      for (let run = 1; run <= runs; run += 1) {
        for (let i = 0; i < tabIds.length; i += 1) {
          if (cancelled) return;
          const tabId = tabIds[i];
          setActiveTab(tabId);
          if (typeof onCaptureProgress === 'function') {
            try { onCaptureProgress({ run, tabIndex: i, tabId, totalTabs: tabIds.length, totalRuns: runs }); }
            catch { /* swallow caller errors so capture continues */ }
          }
          await new Promise((r) => setTimeout(r, dwell));
        }
      }
      if (cancelled) return;
      if (typeof onCaptureComplete === 'function') {
        try { onCaptureComplete({ runs, tabsVisited: tabIds }); }
        catch { /* swallow */ }
      }
    })();
    return () => { cancelled = true; };
  // TABS depends on schedulerRole/allowSettings — recompute on those
  // rather than the array identity (which changes every render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode, captureRuns, captureTabDwellMs, schedulerRole, allowSettings]);

  /* ── Weekly Review state (real-mode; API-backed) ── */
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const loadReview = useCallback(async () => {
    if (isDemoMode()) return;
    setReviewLoading(true);
    setReviewError(null);
    const res = await schedulerApi.getWeeklyReview();
    if (res.ok) {
      setReview(res.data || null);
    } else {
      setReviewError(res.error);
      setReview(null);
    }
    setReviewLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'review' && schedulerRole === 'manager' && !review && !reviewLoading) {
      loadReview();
    }
  }, [activeTab, schedulerRole, review, reviewLoading, loadReview]);

  const applyReviewChanges = async (changes) => {
    if (!changes.length) return;
    const res = await schedulerApi.applyWeeklyReview({ changes });
    if (res.ok) {
      toast.success(`Applied ${res.data?.count ?? changes.length} change${changes.length === 1 ? '' : 's'}`);
      loadReview();
    } else {
      toast.error('Failed to apply', { description: res.error?.message });
    }
  };

  // Roll up every tab's data-*-source into a single pipe-delimited
  // attribute on the root. Each tab keeps its own data-*-source as
  // source-of-truth — this is just for QA grep-ability.
  const schedulerTrace = useSchedulerTraceString();

  // Today tab source — derive from the EchoScheduler's own state (no
  // child needs to call back). Other tabs report from inside their
  // own components via useReportSchedulerSource(...).
  const todaySource =
    todayLoading        ? 'loading'
    : todayError        ? 'error'
    : todayCards.length > 0
      ? ((todaySummary?.booked || 0) > 0 ? 'live' : 'open_only')
      : 'empty';
  useReportSchedulerSource('today', todaySource);
  // Calendar / Insights / Settings — currently always present when
  // their tab is active. Calendar uses the shared SchedulerCalendar
  // which doesn't expose state, so report 'live' once it mounts.
  useReportSchedulerSource('calendar', activeTab === 'calendar' ? 'live' : null);
  useReportSchedulerSource('insights', activeTab === 'analytics' ? 'live' : null);
  useReportSchedulerSource('settings', activeTab === 'settings' && allowSettings ? 'live' : null);

  return (
    <div className="space-y-5" data-testid="echo-scheduler-page" data-scheduler-trace={schedulerTrace}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Schedule</h1>
            <Badge className="text-[9px] border-0 bg-violet-100 text-violet-700 gap-1">
              <Sparkles className="h-2.5 w-2.5" /> Echo-Powered
            </Badge>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">{today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="gap-1.5 px-2 py-1 text-[10px] font-medium border-slate-200 text-slate-500" data-testid="scheduler-role-badge">
            Role: {schedulerRole.replace('_', ' ')}
          </Badge>
          <button
            onClick={() => setCategoryDialog('red')}
            className="gap-1.5 px-2.5 py-1.5 text-[11px] font-medium border border-red-200 text-red-600 bg-red-50 rounded-md inline-flex items-center hover:bg-red-100 transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="punch-red-count"
            aria-label={`${redCount} urgent items — click to review`}
          >
            {redCount} urgent
          </button>
          <button
            onClick={() => setCategoryDialog('amber')}
            className="gap-1.5 px-2.5 py-1.5 text-[11px] font-medium border border-amber-200 text-amber-600 bg-amber-50 rounded-md inline-flex items-center hover:bg-amber-100 transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="punch-amber-count"
            aria-label={`${amberCount} attention items — click to review`}
          >
            {amberCount} attention
          </button>
          <button
            onClick={() => setCategoryDialog('green')}
            className="gap-1.5 px-2.5 py-1.5 text-[11px] font-medium border border-emerald-200 text-emerald-600 bg-emerald-50 rounded-md inline-flex items-center hover:bg-emerald-100 transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="punch-green-count"
            aria-label={`${greenCount} done items — click to review`}
          >
            {greenCount} done
          </button>
          <Button variant="outline" className="text-xs gap-1.5 h-9" onClick={() => setLensOpen(true)} data-testid="scheduler-lens-btn">
            <ScanLine className="h-3.5 w-3.5" /> Upload with Lens
          </Button>
          <Button
            variant="outline"
            className="text-xs gap-1.5 h-9 border-amber-200 text-amber-700 hover:bg-amber-50"
            data-testid="walkin-btn"
            onClick={() => {
              if (!allowBooking) { toast.info('Your role is read-only — ask Front Desk or Admin to book.'); return; }
              setBookingWalkin(true);
              setBookingPrefill(null);
              setBookingOpen(true);
            }}
          >
            <UserPlus className="h-3.5 w-3.5" /> Walk-in
          </Button>
          <Button
            className="bg-[#111] hover:bg-[#222] text-xs gap-1.5 h-9"
            data-testid="new-appointment-btn"
            onClick={() => {
              if (!allowBooking) { toast.info('Your role is read-only — ask Front Desk or Admin to book.'); return; }
              setBookingWalkin(false);
              setBookingPrefill(null);
              setBookingOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> New Appointment
          </Button>
        </div>
      </div>

      {/* Sub-navigation tabs */}
      <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 w-fit" data-testid="scheduler-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-4 py-2 rounded-md text-xs font-medium transition-colors",
              activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
            style={{ border: 'none', cursor: 'pointer' }}
            data-testid={`scheduler-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TODAY VIEW ═══ */}
      {activeTab === 'today' && (
        <div className="flex gap-4" data-testid="today-view">
          <div className="flex-1 min-w-0" data-testid="today-timeline">
            {availabilityPartialWarning && (
              <div
                role="alert"
                data-testid="all-locations-partial-warning"
                className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
              >
                <span className="font-medium shrink-0">Partial availability:</span>
                <span className="leading-snug">{availabilityPartialWarning.message}</span>
              </div>
            )}
            <Card className="border-slate-200" data-testid="today-timeline-card">
              <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <CalIcon className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-800" data-testid="today-timeline-title">Today&apos;s Timeline</p>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-400">
                  <span data-testid="today-timeline-appt-count">
                    {todaySummary.booked || 0} appointment{(todaySummary.booked || 0) === 1 ? '' : 's'}
                  </span>
                  {(todaySummary.cancelled || 0) > 0 && (
                    <span
                      data-testid="today-timeline-cancelled-count"
                      className="text-slate-400"
                      title={`${todaySummary.cancelled} cancelled appointment(s) hidden from grid`}
                    >
                      {todaySummary.cancelled} cancelled hidden
                    </span>
                  )}
                </div>
              </div>
              <CardContent className="p-3">
                <TodaySlotGrid
                  cards={todayCards}
                  summary={todaySummary}
                  entities={entities}
                  entityId={entityFilter}
                  onEntityChange={setEntityFilter}
                  locations={locations}
                  locationId={locationFilter}
                  onLocationChange={setLocationFilter}
                  loading={todayLoading}
                  error={todayError}
                  availabilityError={availabilityError}
                  onRetry={fetchToday}
                  onBook={openBookingFromSlot}
                  onNewBooking={openNewBooking}
                  onWalkin={openWalkinBooking}
                  onCheckin={handleCheckin}
                  onOpen={(card) => setCancelRescheduleApt({
                    id: card.appointmentId || card.id,
                    patient_name: card.patientName || card.patient_name || card.name || card.patient,
                    start: card.startAt || card.start,
                    end: card.endAt || card.end,
                  })}
                  allowBooking={allowBooking}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                />
              </CardContent>
            </Card>
          </div>

          <PunchListRail
            items={punchList}
            loading={todayLoading}
            error={todayError}
            onRetry={fetchToday}
            onAction={handlePunchAction}
          />
        </div>
      )}

      {/* ═══ CALENDAR TAB ═══ */}
      {activeTab === 'calendar' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={CalendarDays}
              title="Calendar"
              subtitle="Day · week · month — multi-provider board"
              testId="real-calendar-section"
            >
              <SchedulerCalendar onBook={(prefill) => { setBookingPrefill(prefill); setBookingWalkin(false); setBookingOpen(true); }} />
            </TabSection>
          </div>
          <div className="w-72 shrink-0" data-testid="calendar-ai-sidebar">
            <SchedulerAIChat compact />
          </div>
        </div>
      )}

      {/* ═══ ROOMING TAB ═══ */}
      {activeTab === 'rooming' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={DoorOpen}
              title="Rooming"
              subtitle="Live queue · room assignments · suggested swaps"
              testId="real-rooming-section"
            >
              {/* Real mode: API-backed via vendor §M9k. The picker derives
                  from the same locationFilter the Today grid uses; fall
                  back to "no location" placeholder until one is chosen. */}
              <RealRoomingTab
                role={schedulerRole}
                locationId={locationFilter}
                providerId={user?.entity_id || null}
                date={selectedDate}
              />
            </TabSection>
          </div>
          {schedulerRole !== 'provider' && (
            <div className="w-64 shrink-0"><SchedulerAIChat compact /></div>
          )}
        </div>
      )}

      {/* ═══ WAITLIST TAB ═══ */}
      {activeTab === 'waitlist' && schedulerRole !== 'ma' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={ClipboardList}
              title="Waitlist"
              subtitle="Ranked patients · best-slot match · backfill actions"
              testId="real-waitlist-section"
            >
              <RealWaitlistTab
                role={schedulerRole}
                locationId={locationFilter}
                onAdd={() => setAddWaitlistOpen(true)}
              />
            </TabSection>
          </div>
          <div className="w-64 shrink-0"><SchedulerAIChat compact /></div>
        </div>
      )}

      {/* ═══ RECALL TAB (campaigns + roster + CTA row — full structural parity) ═══ */}
      {activeTab === 'recall' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={PhoneCall}
              title="Recall"
              subtitle="Campaigns · overdue rosters · outreach"
              testId="real-recall-section"
            >
              <RealCampaignRecallsPanel allowOutreach={allowBooking} />
            </TabSection>
          </div>
          <div className="w-64 shrink-0"><SchedulerAIChat compact /></div>
        </div>
      )}

      {/* ═══ INSIGHTS TAB (real-mode Analytics — vendor §M9e KPIs + overbooking) ═══ */}
      {activeTab === 'analytics' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={LineChart}
              title="Insights"
              subtitle="KPIs · trends · recommendations"
              testId="real-insights-section"
            >
              <div className="space-y-4">
                {/* Overbooking strip (vendor §Analytics) — Legacy parity */}
                <RealOverbookingDashboard allowApply={allowSettings} />
                <AnalyticsTab />
              </div>
            </TabSection>
          </div>
          <div className="w-64 shrink-0"><SchedulerAIChat compact /></div>
        </div>
      )}

      {/* ═══ REVIEW TAB (Weekly Scheduler Review — manager-only, API-backed) ═══ */}
      {activeTab === 'review' && schedulerRole === 'manager' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={LineChart}
              title="Weekly Review"
              subtitle="Manager narrative · capacity recommendations"
              testId="real-review-section"
            >
              <WeeklyReview
                weekStart={review?.week_start}
                narrative={review?.narrative}
                providers={review?.providers || []}
                loading={reviewLoading}
                error={reviewError}
                onRefresh={loadReview}
                onApplyAll={() => {
                  const changes = (review?.providers || [])
                    .filter(p => p.trend !== 'match')
                    .map(p => ({ provider_id: p.id, limit: p.ai_suggested }));
                  applyReviewChanges(changes);
                }}
                onApplyOne={(p) => applyReviewChanges([{ provider_id: p.id, limit: p.ai_suggested }])}
                onKeepOne={(p) => applyReviewChanges([{ provider_id: p.id, limit: p.current_limit }])}
              />
            </TabSection>
          </div>
          <div className="w-64 shrink-0"><SchedulerAIChat compact /></div>
        </div>
      )}

      {/* ═══ SETTINGS TAB ═══ */}
      {activeTab === 'settings' && allowSettings && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <TabSection
              icon={SettingsIcon}
              title="Scheduler Settings"
              subtitle="Providers · locations · visit types · policies"
              testId="real-settings-section"
            >
              <SchedulerSettings />
            </TabSection>
          </div>
          <div className="w-64 shrink-0"><SchedulerAIChat compact /></div>
        </div>
      )}
      {activeTab === 'settings' && !allowSettings && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="scheduler-settings-403">
          <CardContent className="p-8 text-center">
            <p className="text-sm font-medium text-amber-700">Settings require admin access</p>
            <p className="text-[11px] text-amber-600 mt-1">Your role can view schedules but cannot change scheduler configuration.</p>
          </CardContent>
        </Card>
      )}

      <BookingModal
        open={bookingOpen}
        onOpenChange={(v) => { setBookingOpen(v); if (!v) { setBookingWalkin(false); setBookingPrefill(null); } }}
        prefillTime={bookingPrefill?.time}
        prefillProviderId={bookingPrefill?.providerId || entityFilter}
        prefillLocationId={bookingPrefill?.locationId || locationFilter}
        prefillIsWalkin={bookingWalkin}
        selectedDate={selectedDate}
        allowBooking={allowBooking}
      />
      <LensUploadLightbox open={lensOpen} onOpenChange={setLensOpen} />
      <AddToWaitlistPanel
        open={addWaitlistOpen}
        onOpenChange={setAddWaitlistOpen}
        onSubmit={() => toast.info('Waitlist API not yet wired in real mode.')}
        visitTypes={[]}
        providers={[]}
      />
      <PunchListCategoryDialog
        open={!!categoryDialog}
        onOpenChange={(v) => { if (!v) setCategoryDialog(null); }}
        category={categoryDialog}
        items={categoryDialog ? punchList.filter(p => p.priority === categoryDialog) : []}
        onAction={handlePunchAction}
      />
      <CancelRescheduleModal
        open={!!cancelRescheduleApt}
        onClose={() => setCancelRescheduleApt(null)}
        appointment={cancelRescheduleApt}
        onMutated={() => { setCancelRescheduleApt(null); fetchToday?.(); }}
      />
    </div>
  );
};

export default EchoScheduler;
